import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  tomorrow,
  vscDarkPlus,
} from "react-syntax-highlighter/dist/esm/styles/prism";

/**
 * Enhanced MarkdownRenderer Component - Optimized for streaming content
 */
const MarkdownRenderer = ({
  content,
  isDarkMode = true,
  isStreaming = false,
  sources = [], // Add sources prop
}) => {
  // Memoize the processed content to avoid unnecessary re-processing
  const processedContent = useMemo(() => {
    if (!content) return "";

    if (typeof content !== "string") {
      if (Array.isArray(content)) {
        return content
          .map((item) =>
            typeof item === "string" ? item.trim() : String(item)
          )
          .filter((item) => item.length > 0)
          .join("\n\n");
      }
      return String(content).trim();
    }

    return content.trim();
  }, [content]);

  const components = useMemo(
    () => ({
      // Enhanced headings with responsive typography and clean styling
      h1: ({ children, ...props }) => (
        <h1
          className={`text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 mt-6 sm:mt-8 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
          {...props}
        >
          {children}
        </h1>
      ),

      h2: ({ children, ...props }) => (
        <h2
          className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 mt-5 sm:mt-6 ${
            isDarkMode ? "text-gray-100" : "text-gray-800"
          }`}
          {...props}
        >
          {children}
        </h2>
      ),

      h3: ({ children, ...props }) => (
        <h3
          className={`text-lg sm:text-xl font-semibold mb-2 sm:mb-3 mt-4 sm:mt-5 ${
            isDarkMode ? "text-gray-200" : "text-gray-800"
          }`}
          {...props}
        >
          {children}
        </h3>
      ),

      h4: ({ children, ...props }) => (
        <h4
          className={`text-base sm:text-lg font-medium mb-2 mt-3 sm:mt-4 ${
            isDarkMode ? "text-gray-300" : "text-gray-700"
          }`}
          {...props}
        >
          {children}
        </h4>
      ),

      h5: ({ children, ...props }) => (
        <h5
          className={`text-sm sm:text-base font-medium mb-2 mt-3 ${
            isDarkMode ? "text-gray-300" : "text-gray-700"
          }`}
          {...props}
        >
          {children}
        </h5>
      ),

      h6: ({ children, ...props }) => (
        <h6
          className={`text-xs sm:text-sm font-medium mb-2 mt-3 ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
          {...props}
        >
          {children}
        </h6>
      ),

      // Enhanced paragraphs with responsive spacing
      p: ({ children, ...props }) => (
        <p
          className={`mb-3 sm:mb-4 leading-relaxed text-sm sm:text-base ${
            isDarkMode ? "text-gray-300" : "text-gray-700"
          }`}
          {...props}
        >
          {children}
        </p>
      ),

      // Code styling with responsive design
      code: ({ node, inline, className, children, ...props }) => {
        const match = /language-(\w+)/.exec(className || "");
        const language = match ? match[1] : "text";

        if (!inline && match) {
          return (
            <div className="not-prose my-4 sm:my-6 -mx-2 sm:mx-0">
              <SyntaxHighlighter
                style={isDarkMode ? vscDarkPlus : tomorrow}
                language={language}
                PreTag="div"
                customStyle={{
                  borderRadius: "8px",
                  fontSize: "12px",
                  lineHeight: "1.5",
                  padding: "12px",
                  margin: 0,
                  backgroundColor: isDarkMode ? "#111418" : "#f9fafc",
                  border: "none",
                  overflowX: "auto",
                }}
                codeTagProps={{
                  style: {
                    fontSize: "12px",
                    lineHeight: "1.5",
                  },
                }}
                {...props}
              >
                {String(children).replace(/\n$/, "")}
              </SyntaxHighlighter>
            </div>
          );
        }

        return (
          <code
            className={`px-1 py-0.5 rounded text-xs sm:text-sm font-mono break-words ${
              isDarkMode
                ? "bg-gray-800 text-gray-200"
                : "bg-gray-100 text-gray-800"
            }`}
            {...props}
          >
            {children}
          </code>
        );
      },

      // Tables with responsive design and mobile optimization
      table: ({ children, ...props }) => (
        <div className="not-prose overflow-x-auto my-4 sm:my-6 -mx-2 sm:mx-0">
          <table className="min-w-full border-collapse text-sm" {...props}>
            {children}
          </table>
        </div>
      ),

      thead: ({ children, ...props }) => (
        <thead
          className={isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}
          {...props}
        >
          {children}
        </thead>
      ),

      th: ({ children, ...props }) => (
        <th
          className={`px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-xs sm:text-sm ${
            isDarkMode ? "text-gray-200" : "text-gray-900"
          }`}
          {...props}
        >
          {children}
        </th>
      ),

      td: ({ children, ...props }) => (
        <td
          className={`px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm ${
            isDarkMode ? "text-gray-300" : "text-gray-700"
          }`}
          {...props}
        >
          {children}
        </td>
      ),

      // Blockquotes with responsive design
      blockquote: ({ children, ...props }) => (
        <blockquote
          className={`border-l-3 sm:border-l-4 pl-3 sm:pl-4 py-2 my-3 sm:my-4 italic text-sm sm:text-base ${
            isDarkMode
              ? "border-blue-400 bg-gray-900/30 text-gray-300"
              : "border-blue-600 bg-gray-50 text-gray-700"
          }`}
          {...props}
        >
          {children}
        </blockquote>
      ),

      // Links with responsive typography
      a: ({ href, children, ...props }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`underline decoration-2 underline-offset-2 hover:no-underline transition-all duration-200 text-sm sm:text-base break-words ${
            isDarkMode
              ? "text-blue-400 hover:text-blue-300"
              : "text-blue-600 hover:text-blue-700"
          }`}
          {...props}
        >
          {children}
        </a>
      ),

      // Images with responsive design
      img: ({ src, alt, ...props }) => (
        <img
          src={src}
          alt={alt}
          className="max-w-full h-auto rounded-lg shadow-lg my-4 sm:my-6 mx-auto"
          loading="lazy"
          {...props}
        />
      ),

      // Enhanced lists with responsive spacing
      ul: ({ children, ...props }) => (
        <ul
          className={`space-y-1 sm:space-y-2 my-3 sm:my-4 pl-5 sm:pl-6 text-sm sm:text-base ${
            isDarkMode ? "text-gray-300" : "text-gray-700"
          }`}
          style={{ listStyleType: "disc" }}
          {...props}
        >
          {children}
        </ul>
      ),

      ol: ({ children, ...props }) => (
        <ol
          className={`space-y-1 sm:space-y-2 my-3 sm:my-4 pl-5 sm:pl-6 text-sm sm:text-base ${
            isDarkMode ? "text-gray-300" : "text-gray-700"
          }`}
          style={{ listStyleType: "decimal" }}
          {...props}
        >
          {children}
        </ol>
      ),

      li: ({ children, ...props }) => (
        <li className="leading-relaxed" {...props}>
          {children}
        </li>
      ),

      // Enhanced strong/bold text with responsive sizing
      strong: ({ children, ...props }) => (
        <strong
          className={`font-semibold text-sm sm:text-base ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
          {...props}
        >
          {children}
        </strong>
      ),

      // Enhanced emphasis/italic text with responsive sizing
      em: ({ children, ...props }) => (
        <em
          className={`italic text-sm sm:text-base ${
            isDarkMode ? "text-gray-200" : "text-gray-800"
          }`}
          {...props}
        >
          {children}
        </em>
      ),

      // Responsive horizontal spacing
      hr: ({ ...props }) => <div className="my-4 sm:my-6" {...props} />,
    }),
    [isDarkMode]
  );

  return (
    <div className="w-full">
      {/* Sources Section - mobile optimized horizontal scrollable */}
      {sources && sources.length > 0 && (
        <div
          className={`mb-4 sm:mb-6 ${
            isDarkMode ? "text-gray-300" : "text-gray-700"
          }`}
        >
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            <span className="text-base sm:text-lg">🔗</span>
            <h3
              className={`text-base sm:text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Sources
            </h3>
          </div>
          <div
            className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 -mx-2 px-2 sm:-mx-1 sm:px-1"
            style={{
              scrollbarWidth: "thin",
              scrollSnapType: "x mandatory",
            }}
          >
            {sources
              .filter((source) => {
                // Only display sources with HTTPS URLs
                return source.url && source.url.startsWith("https://");
              })
              .map((source, index) => {
                // Extract domain from URL
                let domain = "";
                try {
                  if (source.url) {
                    const url = new URL(source.url);
                    domain = url.hostname.replace("www.", "");
                  }
                } catch (e) {
                  domain = source.url || "";
                }

                return (
                  <div
                    key={index}
                    className={`flex-shrink-0 px-2 sm:px-3 py-2 sm:py-3 rounded-lg border min-w-[140px] sm:min-w-[200px] max-w-[160px] sm:max-w-[250px] ${
                      isDarkMode
                        ? "bg-gray-800/50 border-gray-700 hover:bg-gray-800"
                        : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                    } transition-colors cursor-pointer`}
                    style={{ scrollSnapAlign: "start" }}
                    onClick={() =>
                      source.url && window.open(source.url, "_blank")
                    }
                  >
                    <div className="flex flex-col gap-1">
                      <span
                        className={`text-xs sm:text-sm font-medium line-clamp-2 ${
                          isDarkMode ? "text-gray-200" : "text-gray-800"
                        }`}
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {source.title || source.name}
                      </span>
                      <span
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {domain}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <div
        className={`prose prose-sm sm:prose-lg max-w-none ${
          isDarkMode ? "prose-invert" : ""
        } prose-headings:scroll-mt-16 prose-code:before:content-none prose-code:after:content-none`}
        style={{
          // Override default prose styles to use our custom components
          "--tw-prose-body": isDarkMode ? "#cdd1d6" : "#2a2e33",
          "--tw-prose-headings": isDarkMode ? "#f9fafc" : "#111418",
          "--tw-prose-links": isDarkMode ? "#5ebcff" : "#0593e6",
          "--tw-prose-bold": isDarkMode ? "#f9fafc" : "#111418",
          "--tw-prose-quotes": isDarkMode ? "#cdd1d6" : "#757b81",
          "--tw-prose-quote-borders": isDarkMode ? "#36acff" : "#36acff",
          "--tw-prose-captions": isDarkMode ? "#a0a5ab" : "#757b81",
          "--tw-prose-code": isDarkMode ? "#e2e5e8" : "#111418",
          "--tw-prose-pre-code": isDarkMode ? "#e2e5e8" : "#111418",
          "--tw-prose-pre-bg": isDarkMode ? "#181c20" : "#f9fafc",
          "--tw-prose-th-borders": isDarkMode ? "#2a2e33" : "#cdd1d6",
          "--tw-prose-td-borders": isDarkMode ? "#2a2e33" : "#e2e5e8",
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, rehypeSanitize]}
          components={components}
          skipHtml={false}
        >
          {processedContent}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default MarkdownRenderer;
